/**
 * Rules for /mcp-authorize, the page an AI assistant (Claude, ChatGPT, …) sends someone to when they connect Rithmo.
 * Pure functions so the page stays thin and the rules are testable.
 *
 * Contract (hosted OAuth, src/api/routers/mcpOAuthSignInRouter.ts):
 *   GET  /api/mcp/oauth/sign-in       WorkOS lands here and bounces to this page, keeping its query (external_auth_id).
 *   GET  /api/mcp/oauth/connections   the signed-in person's eligible connections: { connections: [{ connectionId, orgId, label }] }
 *   POST /api/mcp/oauth/sign-in       { connectionId, externalAuthId } -> { redirect_uri } (back to the AI platform)
 * The backend decides eligibility (membership + an active OAuth connection) and binds the verified AI client itself.
 * It does not tell this page which AI platform is asking, so the page never names one.
 */

export const ASSISTANT = 'Your AI assistant'
export const assistant = 'your AI assistant'

/** The WorkOS handle that ties this page to the AI platform's authorization request. Nothing else is read. */
export function readHandoff(search) {
  const id = new URLSearchParams(search || '').get('external_auth_id')
  return { externalAuthId: id && id.trim() ? id.trim() : null }
}

/**
 * One choice per eligible connection, named by the company (from the person's own memberships, GET /org/list).
 * A connection's label is shown only when a company has more than one, so they can be told apart.
 */
export function companyChoices(connections, orgs) {
  const names = new Map((orgs || []).map((o) => [String(o.id), o.name]))
  const list = (connections || []).filter((c) => c && c.connectionId && c.orgId)
  const perOrg = list.reduce((m, c) => m.set(c.orgId, (m.get(c.orgId) || 0) + 1), new Map())
  return list
    .map((c) => ({
      connectionId: c.connectionId,
      orgId: c.orgId,
      company: names.get(String(c.orgId)) || c.label || 'Your company',
      detail: perOrg.get(c.orgId) > 1 ? (c.label || 'Unnamed connection') : null,
      // The EXACT permissions this connection grants, from the server's current policy. The page DISPLAYS these and
      // sends them back as `reviewedScopes` so the approval is bound to what was reviewed (no silent broadening).
      scopes: Array.isArray(c.scopes) ? c.scopes : [],
    }))
    .sort((a, b) => a.company.localeCompare(b.company) || String(a.detail).localeCompare(String(b.detail)))
}

/**
 * The human-readable permissions a hosted connection grants, from its scopes. Shown on the consent page so the person
 * sees EXACTLY what they allow; `reporting` explains the mcp:report permission explicitly when present. Unknown scopes
 * are surfaced verbatim rather than hidden, so consent is never broader than what is displayed.
 */
export function permissionLines(scopes) {
  const set = new Set(scopes || [])
  const known = {
    'mcp:read': 'Read your organization’s decision records to answer and check questions.',
    'mcp:report': 'Report back whether it carried out an action after Rithmo allowed it (recorded as the assistant’s own account, not as proof).',
    'mcp:agents:write': 'Add agent profiles of your own on this connection and set their purpose. It can never change instructions (you or an org admin set those), another person’s agents, your policy, sources or decision records.',
  }
  const lines = []
  for (const s of set) lines.push({ scope: s, text: known[s] || `Use the “${s}” permission.` })
  // Deterministic order: read first, report next, manage, then any others alphabetically.
  const rank = (s) => (s === 'mcp:read' ? 0 : s === 'mcp:report' ? 1 : s === 'mcp:agents:write' ? 2 : 3)
  return lines.sort((a, b) => rank(a.scope) - rank(b.scope) || a.scope.localeCompare(b.scope))
}

/** Whether this connection would let the assistant REPORT its outcomes (needs explicit explanation on the page). */
export function grantsReporting(scopes) {
  return (scopes || []).includes('mcp:report')
}

/** Why nothing can be connected, in plain English. `orgs` are the person's memberships. */
export function explainNoChoices(orgs) {
  const companies = (orgs || []).filter((o) => !o.isPersonal)
  if (companies.length === 0) {
    return {
      title: `${ASSISTANT} can't connect yet`,
      body: "Your Rithmo account isn't part of a company yet. Ask a Rithmo admin at your company to invite you, then connect again from your AI assistant.",
    }
  }
  const adminOf = companies.filter((o) => ['org_admin', 'admin', 'owner'].includes(o.role)).map((o) => o.name)
  return {
    title: `${ASSISTANT} can't connect yet`,
    body: 'Rithmo hasn’t been set up for AI assistants at your company. A Rithmo admin at your company needs to set this up first. Once it’s set up, connect again from your AI assistant.',
    // Setting it up from the dashboard doesn't exist yet, so an admin is pointed to a person, not a button.
    adminNote: adminOf.length
      ? `You're an admin at ${adminOf.join(', ')}, but this can't be set up from the dashboard yet. Your Rithmo contact can set it up for you.`
      : null,
  }
}

/** The page's view of a failed GET /mcp/oauth/connections. */
export function explainListError(err) {
  if (err?.status === 404) {
    return { title: "This isn't available yet", body: "Connecting AI assistants isn't turned on for this Rithmo site yet.", retry: false }
  }
  return { title: "Rithmo couldn't load your companies", body: 'Check your connection and try again.', retry: true }
}

/** The page's view of a failed POST /mcp/oauth/sign-in. `signedOut` means the session ended: sign in again. */
export function explainApproveError(err, company) {
  const status = err?.status
  const code = typeof err?.body?.error === 'string' ? err.body.error : null
  if (status === 401) return { signedOut: true }
  if (status === 403 || code === 'not_eligible') {
    return {
      title: `You can't connect ${company || 'this company'}`,
      body: 'Your access may have changed. Choose another company, or ask a Rithmo admin there.',
      retry: false,
      reload: true,
    }
  }
  if (status === 409 || code === 'scopes_changed') {
    // The connection's permissions were changed while this page was open. Reload re-displays the NEW permissions; the
    // approval only ever binds what is currently shown, so consent can never be silently broadened.
    return {
      title: 'The permissions changed',
      body: 'What this assistant can do was updated while this page was open. Review the new permissions above, then allow it again.',
      retry: true,
      reload: true,
    }
  }
  if (status === 400) {
    return { title: 'This link is incomplete', body: 'Go back to your AI assistant and connect again.', retry: false }
  }
  if (status === 404) {
    return { title: "This isn't available yet", body: "Connecting AI assistants isn't turned on for this Rithmo site yet.", retry: false }
  }
  if (status === 500 || status === 503) {
    return {
      title: "Rithmo can't finish connecting right now",
      body: 'This is a setup problem on Rithmo’s side, not something you did. Your Rithmo contact can fix it.',
      retry: false,
    }
  }
  if (status === 502) {
    return {
      title: "Rithmo couldn't finish connecting",
      body: 'If this page was open for a while, the request may have expired. Go back to your AI assistant and connect again.',
      retry: true,
    }
  }
  return { title: 'Something went wrong', body: 'Check your connection and try again.', retry: true }
}

/** Only follow a web address back to the AI platform; anything else is treated as a failure. */
export function safeReturnUrl(value) {
  if (typeof value !== 'string') return null
  try {
    const u = new URL(value)
    return u.protocol === 'https:' || u.protocol === 'http:' ? u.toString() : null
  } catch {
    return null
  }
}

/**
 * The address analytics may record for a route. The authorization handle (external_auth_id) and the SSO completion code
 * (sso_code) must never reach analytics — on /mcp-authorize the WHOLE query is the authorization handoff, so it is
 * dropped; on any OTHER route those params (and a handle/code nested inside a return_to) are scrubbed as defense in
 * depth, so the journey (login / invite / verify / SSO callbacks) can never leak them even through a nested return URL.
 */
const ANALYTICS_SENSITIVE_QS = ['external_auth_id', 'sso_code']
export function analyticsPath(pathname, search) {
  if (pathname === '/mcp-authorize') return pathname // its whole query is the handoff
  if (!search) return pathname
  let p
  try {
    p = new URLSearchParams(search)
  } catch {
    return pathname // unparseable → record only the path
  }
  let changed = false
  for (const k of ANALYTICS_SENSITIVE_QS) {
    if (p.has(k)) { p.delete(k); changed = true }
  }
  const rt = p.get('return_to')
  if (rt && ANALYTICS_SENSITIVE_QS.some((k) => rt.includes(k))) { p.set('return_to', '[redacted]'); changed = true }
  if (!changed) return `${pathname}${search}`
  const qs = p.toString()
  return qs ? `${pathname}?${qs}` : pathname
}
